import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import Button from "../components/Button";
import {
  faSearch,
  faSortUp,
  faSortDown,
  faAngleDoubleRight,
  faDownload,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faSearch,
  faSortUp,
  faSortDown,
  faAngleDoubleRight,
  faDownload,
  faChevronLeft,
  faChevronRight
);

export default function Table(props) {
  const {
    keyField,
    boardTable,
    showMore,
    caption,
    columns,
    loading,
    noDataMessage,
    showMorePagePath,
    t
  } = props;

  const [data, setData] = useState(props.data);
  const [sortField, setSortField] = useState(keyField);
  const [sortOrder, setSortOrder] = useState("desc");

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  const handleSort = (field) => {
    const newOrder = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(newOrder);

    const sortedData = [...data].sort((a, b) => {
      let aVal = a[field];
      let bVal = b[field];

      // Handle null/undefined
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Convert to string for comparison if needed
      if (typeof aVal === "string") aVal = aVal.toLowerCase();
      if (typeof bVal === "string") bVal = bVal.toLowerCase();

      if (aVal < bVal) return newOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return newOrder === "asc" ? 1 : -1;
      return 0;
    });

    setData(sortedData);
  };

  const exportToCSV = () => {
    // Create CSV content
    const headers = columns.map(col => col.text || col.dataField).join(",");
    const rows = data.map(row =>
      columns.map(col => {
        const value = row[col.dataField] || "";
        // Escape quotes and wrap in quotes if contains comma
        return typeof value === "string" && (value.includes(",") || value.includes('"'))
          ? `"${value.replace(/"/g, '""')}"`
          : value;
      }).join(",")
    );

    const csv = [headers, ...rows].join("\n");

    // Download
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = props.csvFileName || "export.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const ExportCSV = () => {
    return (
      <Button
        className="button-color--invertTurquoisepale my-2 py-1 mr-3 rounded-lg"
        aria-label="Exporter au format CSV"
        onClick={exportToCSV}
      >
        <FontAwesomeIcon
          icon={faDownload}
          size="2x"
          style={{ height: "25px" }}
          className="mx-1"
        />
      </Button>
    );
  };

  const { page, pageSize, totalCount, onPageChange } = props;
  const totalPages = pageSize > 0 ? Math.ceil((totalCount || 0) / pageSize) : 0;
  const canPage = page !== undefined && pageSize !== undefined && totalPages > 0;

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    onPageChange && onPageChange(newPage);
  };

  const from = canPage ? (page - 1) * pageSize + 1 : 0;
  const to = canPage ? Math.min(page * pageSize, totalCount || 0) : 0;

  const Pagination = () => (
    <div
      className="flex items-center justify-between py-3"
      aria-label="Pagination"
    >
      <span className="text-sm text-black">
        {t("Affiche {{from}}-{{to}} sur {{total}} enregistrements", {
          from,
          to,
          total: totalCount || 0
        })}
      </span>
      <div className="flex items-center space-x-2">
        <Button
          className="border border-donaPurple rounded px-3 py-1 mx-2 text-donaPurple font-bold hover:bg-donaGray disabled:opacity-40"
          aria-label={t("Précédent")}
          disabled={page <= 1}
          onClick={() => handlePageChange(page - 1)}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="mx-1" />
        </Button>
        <span className="text-sm text-black">
          {page} / {totalPages}
        </span>
        <Button
          className="border border-donaPurple rounded px-3 py-1 mx-2 text-donaPurple font-bold hover:bg-donaGray disabled:opacity-40"
          aria-label={t("Suivant")}
          disabled={page >= totalPages}
          onClick={() => handlePageChange(page + 1)}
        >
          <FontAwesomeIcon icon={faChevronRight} className="mx-1" />
        </Button>
      </div>
    </div>
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSortUp} size="2x" className="mx-2" />;
    }
    return (
      <FontAwesomeIcon
        icon={sortOrder === "asc" ? faSortUp : faSortDown}
        size="2x"
        className="mx-2"
      />
    );
  };

  return (
    <>
      {loading ? (
        <>
          {caption}
          {t("En chargement...")}
          <FontAwesomeIcon icon="spinner" size="xs" spin className="mt-2" />
        </>
      ) : (
        <div className={boardTable ? "overflow-x-auto mb-6" : "overflow-x-auto"}>
          {caption}
          <table className="w-full">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.dataField}
                    onClick={() => col.sort !== false && handleSort(col.dataField)}
                    style={{ cursor: col.sort !== false ? "pointer" : "default" }}
                    className={col.headerClasses || "text-left p-2"}
                    {...col.headerAttrs}
                  >
                    {col.text || col.dataField}
                    {col.sort !== false && <SortIcon field={col.dataField} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center p-4">
                    {noDataMessage}
                  </td>
                </tr>
              ) : (
                data.map((row, rowIndex) => (
                  <tr
                    key={row[keyField] || rowIndex}
                    style={{
                      backgroundColor: rowIndex % 2 === 0 ? "transparent" : "#EAEBEC",
                      borderTop: "none"
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.dataField}
                        className={col.classes || "p-2"}
                        {...col.attrs}
                      >
                        {col.formatter
                          ? col.formatter(row[col.dataField], row, rowIndex)
                          : row[col.dataField]}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {canPage && <Pagination />}
          {boardTable && showMore && (
            <div className="flex py-2 justify-center mb-2">
              <Link
                className="underline font-bold text-donaPurple"
                to={showMorePagePath}
              >
                {t("Voir Plus")}
                <FontAwesomeIcon
                  icon={faAngleDoubleRight}
                  size="1x"
                  className="mx-1 underline"
                />
              </Link>
            </div>
          )}
          {!boardTable &&
            columns[0]?.dataField !== "titre_produit" &&
            columns[0]?.dataField !== "titre_document" && (
              <ExportCSV />
            )}
        </div>
      )}
    </>
  );
}

Table.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  keyField: PropTypes.string.isRequired,
  noDataMessage: PropTypes.string,
  search: PropTypes.bool,
  boardTable: PropTypes.bool,
  showMore: PropTypes.bool,
  csvFileName: PropTypes.string,
  caption: PropTypes.node,
  loading: PropTypes.bool,
  showMorePagePath: PropTypes.string,
  t: PropTypes.func,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  totalCount: PropTypes.number,
  onPageChange: PropTypes.func
};
